import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { useDeletePayment } from '@/features/finance/api/useFinance'
import { formatMoney } from '@/lib/format'
import type { Payment } from '@/types/domain'

/* «Отклонить» is a DELETION with a stated cause (spec L8), asked for in a sheet
 * with a real field rather than through a browser confirm: the reason is the
 * only thing that will survive the row — it goes into the audit trail, which is
 * the one place a fact about a row that no longer exists can live — so it has
 * to be typed, and a native dialog cannot take text, cannot be translated and
 * cannot be reached by the keyboard conventions the rest of this product uses.
 *
 * ONE component for the two places the act is offered — the case's payments
 * panel and the direction page's settlements table — so the wording, the
 * mandatory-reason rule and the confirmed-write discipline cannot drift between
 * them. The recognised debt survives the deletion (the server's L8 rule). Since
 * S-18a one carrier payment can have recognised SEVERAL debts — three trucks
 * paid by one transfer — so the sheet lists every cost line the payment
 * settled («Долги останутся: …», label and amount from its allocations) before
 * the reason field: what stays is said before the reader commits to what goes.
 */
export function RejectPaymentSheet({
  open,
  onClose,
  payment,
  verb = 'reject',
}: {
  open: boolean
  onClose: () => void
  payment: Payment
  /** `reject` is Finance deciding somebody else's record; `delete` is the
   *  recorder of a pending client payment removing their own (S-21). The
   *  request is the same deletion with a reason. */
  verb?: 'reject' | 'delete'
}) {
  const { t } = useTranslation()
  const reject = useDeletePayment()
  const [reason, setReason, reasonTouched, markReasonTouched] = useFormDraft(
    open ? payment.id : null,
    () => '',
  )

  /* The cost lines this payment settled — each a debt that outlives it (L8).
     Tranches and invoices are the client's side and are not debts. */
  const debts = payment.allocations.filter((allocation) => allocation.targetKind === 'expense')

  const submit = () => {
    markReasonTouched()
    if (reason.trim() === '') return
    reject.mutate(
      { paymentId: payment.id, caseId: payment.caseId, reason: reason.trim() },
      { onSuccess: onClose },
    )
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={
        verb === 'delete'
          ? t('finance.payments.deleteTitle', { reference: payment.reference })
          : t('finance.payments.rejectTitle', { reference: payment.reference })
      }
      submitLabel={
        verb === 'delete' ? t('finance.payments.deleteSubmit') : t('finance.payments.rejectSubmit')
      }
      pending={reject.isPending}
      dirty={reason.trim() !== ''}
      error={
        reject.error
          ? refusalMessage(reject.error, t)
          : reasonTouched && reason.trim() === ''
            ? t('common.form.required')
            : undefined
      }
      onSubmit={submit}
    >
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {/* Money received settles the client's documents, not a carrier's
            debt: the debt that outlives it is theirs to pay again. */}
        {payment.direction === 'in'
          ? t('finance.payments.rejectBodyIn')
          : t('finance.payments.rejectBody')}
      </p>

      {debts.length > 0 ? (
        <section className="flex flex-col gap-1.5">
          <h3 id={`reject-debts-${payment.id}`} className="asa-eyebrow">
            {t('finance.payments.rejectDebtsStay')}
          </h3>
          <ul aria-labelledby={`reject-debts-${payment.id}`} className="flex flex-col">
            {debts.map((allocation) => (
              <li
                key={allocation.id}
                className="border-line-hair flex items-baseline justify-between gap-3 border-b py-1.5 text-[13px] last:border-b-0"
              >
                <span className="text-text-body min-w-0 [overflow-wrap:anywhere]">{allocation.targetLabel}</span>
                <span className="asa-num text-text-heading shrink-0 text-right whitespace-nowrap">
                  {formatMoney(allocation.amount.amount, allocation.amount.currency)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Field
        label={t('finance.payments.rejectReason')}
        required
        error={reasonTouched && reason.trim() === '' ? t('common.form.required') : undefined}
        hint={t('finance.payments.rejectReasonHint')}
      >
        {(props) => (
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}
